import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Fade,
  Zoom,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Category as CategoryIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  EmojiEvents as TrophyIcon,
  TrendingUp as TrendingUpIcon,
  Lightbulb as LightbulbIcon,
  Star as StarIcon,
  Info as InfoIcon,
  PlayCircleFilled as PlayIcon,
  Celebration as CelebrationIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { TABLES, RELATIONS } from '../lib/tables';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const steps = ['Tip Credit', 'Selectare Modul', 'Activitate și Ore', 'Confirmare'];
const stepsShort = ['Tip', 'Modul', 'Activitate', 'Confirmare'];

export default function NewRequest() {
  const navigate = useNavigate();
  const { profile, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [creditTypes, setCreditTypes] = useState([]);
  const [modules, setModules] = useState([]);
  const [predefinedActivities, setPredefinedActivities] = useState([]);
  
  const [formData, setFormData] = useState({
    id_tip_credit: '',
    id_module: '',
    id_activitate_predefinita: '',
    nume_activitate: '',
    descriere: '',
    ore_realizate: '',
    credite_estimate: 0,
    credite_per_ora: 0.10, 
  });
  
  const [activeStep, setActiveStep] = useState(0);
  const [selectedActivityDetails, setSelectedActivityDetails] = useState(null);

  useEffect(() => {
    let isMounted = true;
    loadDropdownData(isMounted);
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (formData.id_tip_credit && formData.id_module) {
      loadPredefinedActivities(isMounted);
    }
    return () => { isMounted = false; };
  }, [formData.id_tip_credit, formData.id_module]);

  // precompletare din sugestii: /new-request?modul=<id>&tip=<id>
  useEffect(() => {
    if (modules.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const tipParam = parseInt(params.get('tip'), 10);
    const modulParam = parseInt(params.get('modul'), 10);
    if (!tipParam || !modulParam) return;
    if (!creditTypes.some((t) => t.id === tipParam)) return;
    if (!modules.some((m) => m.id === modulParam)) return;
    setFormData((prev) => ({
      ...prev,
      id_tip_credit: tipParam,
      id_module: modulParam,
      id_activitate_predefinita: '',
      nume_activitate: '',
      credite_per_ora: 0.10,
    }));
    setActiveStep(2);
  }, [modules, creditTypes]);

  const loadDropdownData = async (isMounted = true) => {
    try {
      const { data: typesData } = await supabase
        .from(TABLES.credit_types)
        .select('*')
        .order('id');
      
      if (!isMounted) return;
      
      const { data: modulesData } = await supabase
        .from(TABLES.modules)
        .select('*')
        .eq('este_activ', true)
        .order('ordine');
      
      if (!isMounted) return;
      
      setCreditTypes(typesData || []);
      setModules(modulesData || []);
    } catch (error) {
      console.error('Error loading dropdown data:', error);
    }
  };

  const loadPredefinedActivities = async (isMounted = true) => {
    try {
      const { data: activities } = await supabase
        .from(TABLES.predefined_activities)
        .select(`
          *,
          ${RELATIONS.credit_types}(nume_tip)
        `)
        .eq('id_tip_credit', formData.id_tip_credit)
        .eq('id_module', formData.id_module)
        .eq('este_activ', true)
        .order('nume_activitate');
      
      if (isMounted) setPredefinedActivities(activities || []);
    } catch (error) {
      console.error('Error loading predefined activities:', error);
    }
  };

  const handleCreditTypeSelect = (typeId) => {
    setFormData({
      ...formData,
      id_tip_credit: typeId,
      id_module: '',
      id_activitate_predefinita: '',
      nume_activitate: '',
      credite_per_ora: 0.10, 
    });
    setSelectedActivityDetails(null);
  };

  const handleModuleSelect = (moduleId) => {
    setFormData({
      ...formData,
      id_module: moduleId,
      id_activitate_predefinita: '',
      nume_activitate: '',
      credite_per_ora: 0.10, 
    });
    setSelectedActivityDetails(null);
  };

  const handleActivitySelect = (activityId) => {
    const selectedActivity = predefinedActivities.find(a => a.id === activityId);
    if (selectedActivity) {
      const hours = parseInt(formData.ore_realizate) || 0;
      const estimatedCredits = calculateCredits(hours, selectedActivity.credite_per_ora);
      
      setFormData({
        ...formData,
        id_activitate_predefinita: activityId,
        nume_activitate: selectedActivity.nume_activitate,
        descriere: selectedActivity.descriere || formData.descriere,
        credite_estimate: estimatedCredits,
        credite_per_ora: selectedActivity.credite_per_ora || 0.10,
      });
      
      setSelectedActivityDetails(selectedActivity);
    }
  };

  const calculateCredits = (hours, creditsPerHour) => {
    if (!hours || !creditsPerHour) return 0;
    const calculated = hours * creditsPerHour;
    return Math.max(1, Math.ceil(calculated));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'ore_realizate') {
      const hours = parseInt(value) || 0;
      const creditsPerHour = selectedActivityDetails?.credite_per_ora || formData.credite_per_ora || 0.10;
      const estimatedCredits = calculateCredits(hours, creditsPerHour);
      
      setFormData({
        ...formData,
        [name]: value,
        credite_estimate: estimatedCredits,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleCustomActivityName = (e) => {
    setFormData({
      ...formData,
      nume_activitate: e.target.value,
      id_activitate_predefinita: '', 
      credite_per_ora: 0.10, 
    });
    
    setSelectedActivityDetails(null);
  };

  const handleNext = () => {
    if (activeStep === 0 && !formData.id_tip_credit) {
      setError('Selectează tipul creditului pentru a continua!');
      return;
    }
    if (activeStep === 1 && !formData.id_module) {
      setError('Alege un modul pentru a continua!');
      return;
    }
    if (activeStep === 2 && !formData.nume_activitate.trim()) {
      setError('Selectează sau scrie un nume pentru activitate!');
      return;
    }
    if (activeStep === 2 && !formData.ore_realizate) {
      setError('Specifică numărul de ore realizate!');
      return;
    }
    if (activeStep === 2 && parseInt(formData.ore_realizate) < 1) {
      setError('Numărul de ore trebuie să fie cel puțin 1!');
      return;
    }

    setError('');
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!profile) {
        throw new Error('Eroare de profil. Reîncarcă pagina!');
      }

      const { data, error: submitError } = await supabase
        .from(TABLES.credit_requests)
        .insert({
          id_elev: profile.id,
          id_tip_credit: formData.id_tip_credit,
          id_module: formData.id_module,
          id_activitate_predefinita: formData.id_activitate_predefinita || null,
          nume_activitate: formData.nume_activitate,
          descriere: formData.descriere,
          ore_realizate: parseInt(formData.ore_realizate),
          credite_acordate: formData.credite_estimate,
          status: 'in_asteptare',
        })
        .select()
        .single();

      if (submitError) throw submitError;

      await supabase
        .from(TABLES.notifications)
        .insert({
          id_user: profile.id,
          titlu: 'Cerere Trimisă!',
          mesaj: `Cererea ta "${formData.nume_activitate}" a fost trimisă cu succes! Vei primi un răspuns în curând.`,
          tip: 'success',
        });

      setSuccess('Bravo! Cererea a fost trimisă cu succes!');

      setTimeout(() => {
        setFormData({
          id_tip_credit: '',
          id_module: '',
          id_activitate_predefinita: '',
          nume_activitate: '',
          descriere: '',
          ore_realizate: '',
          credite_estimate: 0,
          credite_per_ora: 0.10,
        });
        setSelectedActivityDetails(null);
        setActiveStep(0);
        navigate('/my-requests');
      }, 2000);

    } catch (err) {
      setError(`${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatCreditsPerHour = (creditsPerHour) => {
    if (creditsPerHour === 0.10) return "1 credit / 10 ore";
    if (creditsPerHour === 1.00) return "1 credit / oră";
    if (creditsPerHour === 2.00) return "2 credite / oră";
    if (creditsPerHour === 1.50) return "1.5 credite / oră";
    return `${creditsPerHour} credite / oră`;
  };

  if (authLoading || !profile) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} thickness={4} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Zoom in={true}>
        <Box textAlign="center" mb={3}>
          <Typography 
            variant="h3" 
            fontWeight="bold" 
            gutterBottom
            sx={{ 
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '1.75rem', sm: '3rem' },
              lineHeight: 1.2,
            }}
          >
            <CelebrationIcon sx={{ fontSize: { xs: 28, sm: 40 }, verticalAlign: 'middle', mr: 2 }} />
            Cerere Credit Nou
          </Typography>
          {/* <Typography variant="h6" color="text.secondary">
            Fă-ți activitățile să conteze!
          </Typography> */}
        </Box>
      </Zoom>

      {/* Progres bar */}
      <Box sx={{ mb: 4, px: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: { xs: 0.5, sm: 1 }, mb: 1 }}>
          {steps.map((step, index) => (
            <Box key={index} sx={{ textAlign: 'center', minWidth: 0 }}>
              <Avatar
                sx={{
                  bgcolor: activeStep >= index ? '#4CAF50' : '#e0e0e0',
                  color: activeStep >= index ? 'white' : '#666',
                  width: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: 'bold',
                  mx: 'auto',
                  transition: 'all 0.3s',
                  transform: activeStep === index ? 'scale(1.1)' : 'scale(1)',
                }}
              >
                {index + 1}
              </Avatar>
              <Typography
                variant="caption"
                display="block"
                mt={0.75}
                sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' }, lineHeight: 1.25, px: 0.25, wordBreak: 'break-word', minHeight: { xs: 28, sm: 20 } }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{step}</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{stepsShort[index]}</Box>
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ width: '100%', bgcolor: '#e0e0e0', borderRadius: 5, height: 8, mt: 2 }}>
          <Box 
            sx={{ 
              width: `${((activeStep + 1) / steps.length) * 100}%`, 
              bgcolor: '#4CAF50', 
              height: '100%', 
              borderRadius: 5,
              transition: 'width 0.5s ease-in-out'
            }} 
          />
        </Box>
      </Box>

      <Fade in={true} timeout={500}>
        <Paper 
          elevation={8} 
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 }, 
            borderRadius: 4,
            background: 'linear-gradient(145deg, #ffffff 0%, #f5f9ff 100%)',
            border: '1px solid #e0f2fe',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative elements */}
          <Box sx={{ position: 'absolute', top: 0, right: 0, opacity: 0.1 }}>
            <TrophyIcon sx={{ fontSize: 200, color: '#2196F3' }} />
          </Box>

          {/* Mesaje */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: 3 }}
              icon={<InfoIcon />}
            >
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert 
              severity="success" 
              sx={{ mb: 3, borderRadius: 3 }}
              icon={<CelebrationIcon />}
            >
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {activeStep === 0 && (
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom color="primary">
                  <CategoryIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                  1. Ce fel de activitate ai făcut?
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Alege tipul de credit care se potrivește cu activitatea ta
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%' }}>
                  {creditTypes.map((type) => (
                    <Box key={type.id} sx={{ flex: '0 0 100%', display: 'flex' }}>
                      <Card
                        onClick={() => handleCreditTypeSelect(type.id)}
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                          border: formData.id_tip_credit === type.id ? '3px solid #4CAF50' : '2px solid #e0e0e0',
                          transform: formData.id_tip_credit === type.id ? 'translateY(-5px)' : 'none',
                          boxShadow: formData.id_tip_credit === type.id ? 6 : 1,
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: 6,
                            borderColor: '#2196F3',
                          },
                          borderRadius: 3,
                          overflow: 'hidden',
                          position: 'relative',
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          height: { xs: 'auto', sm: 220 },
                          minHeight: { xs: 190, sm: 220 },
                        }}
                      >
                        {formData.id_tip_credit === type.id && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 10,
                              right: 10,
                              bgcolor: '#4CAF50',
                              color: 'white',
                              borderRadius: '50%',
                              width: 30,
                              height: 30,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </Box>
                        )}
                        
                        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center', p: { xs: 2, sm: 2.5 } }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1.5 }}>
                            <Avatar
                              sx={{
                                bgcolor: type.culoare || '#4CAF50',
                                mb: 1,
                                width: { xs: 44, sm: 50 },
                                height: { xs: 44, sm: 50 },
                              }}
                            >
                              {type.id === 1 && <SchoolIcon />}
                              {type.id === 2 && <StarIcon />}
                              {type.id === 3 && <TrendingUpIcon />}
                            </Avatar>
                            <Box>
                              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                {type.nume_tip}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Validare: {type.validator_rol}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                            {type.descriere || `Activități de tip ${type.nume_tip.toLowerCase()}`}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {activeStep === 1 && (
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom color="primary">
                  <SchoolIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                  2. Alege modulul
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Pentru ce modul vrei să primești credite?
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%' }}>
                  {modules.map((module) => (
                    <Box key={module.id} sx={{ flex: '0 0 100%', display: 'flex' }}>
                      <Card
                        onClick={() => handleModuleSelect(module.id)}
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                          border: formData.id_module === module.id ? '3px solid #2196F3' : '1px solid #e0e0e0',
                          bgcolor: formData.id_module === module.id ? '#f0f7ff' : 'white',
                          '&:hover': {
                            borderColor: '#2196F3',
                            bgcolor: '#f5f9ff',
                          },
                          borderRadius: 2,
                          width: '100%',
                          height: '100%',
                          minHeight: 140,
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                        }}
                      >
                        <CardContent sx={{ 
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          p: 2
                        }}>
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Box>
                                <Typography variant="h6" color="primary" fontWeight="bold">
                                  {module.cod_module}
                                </Typography>
                                <Typography variant="subtitle2" fontWeight="medium">
                                  {module.nume_module}
                                </Typography>
                              </Box>
                              <Avatar sx={{ bgcolor: '#2196F3', width: 32, height: 32 }}>
                                <SchoolIcon fontSize="small" />
                              </Avatar>
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.85rem' }}>
                              {module.descriere}
                            </Typography>
                          </Box>
                          
                          {formData.id_module === module.id && (
                            <Box sx={{ 
                              position: 'absolute', 
                              bottom: 2,
                              left: '50%', 
                              transform: 'translateX(-50%)',
                              zIndex: 1,
                            }}>
                              <Chip 
                                label="Selectat" 
                                color="success" 
                                size="small"
                                sx={{ 
                                  fontWeight: 'medium',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                }}
                              />
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {activeStep === 2 && (
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom color="primary">
                  <DescriptionIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                  3. Alege activitatea și specifică orele
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Selectează o activitate predefinită (cu valoare specifică) sau scrie una personalizată
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%' }}>
                  {predefinedActivities.length > 0 && (
                    <Box sx={{ width: '100%' }}>
                      <FormControl fullWidth>
                        <InputLabel>Alege o activitate predefinită</InputLabel>
                        <Select
                          value={formData.id_activitate_predefinita}
                          onChange={(e) => handleActivitySelect(e.target.value)}
                          label="Alege o activitate predefinită"
                        >
                          <MenuItem value="">
                            <em>Selectează o activitate din listă...</em>
                          </MenuItem>
                          {predefinedActivities.map((activity) => (
                            <MenuItem key={activity.id} value={activity.id}>
                              <Typography>
                                {activity.nume_activitate}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                         Fiecare activitate are o valoare specifică de credite/oră
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ minHeight: selectedActivityDetails ? 100 : 0 }}>
                      {selectedActivityDetails && (
                        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#4CAF50', bgcolor: '#f8fdf8' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, alignItems: 'center' }}>
                              <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                  {selectedActivityDetails.nume_activitate}
                                </Typography>
                                {selectedActivityDetails.descriere && (
                                  <Typography variant="body2" color="text.secondary">
                                    {selectedActivityDetails.descriere}
                                  </Typography>
                                )}
                              </Box>
                              <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                {selectedActivityDetails.ore_minime && (
                                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                    Minim {selectedActivityDetails.ore_minime} ore
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      )}
                    </Box>
                  </Box>

                  {/* Câmp pentru nume activitate personalizat */}
                  <Box sx={{ width: '100%', p: 2 }}>
                    <TextField
                      fullWidth
                      label="Sau scrie o activitate personalizată *"
                      name="nume_activitate"
                      value={formData.nume_activitate}
                      onChange={handleCustomActivityName}
                      placeholder="ex: Proiect de cercetare despre Inteligența Artificială"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                        }
                      }}
                      InputProps={{
                        startAdornment: <PlayIcon color="action" sx={{ mr: 1 }} />,
                      }}
                    />
                  </Box>

                  {/* Câmp pentru ore realizate */}
                  <Box sx={{ flex: '0 0 100%', display: 'flex', p: 2 }}>
                    <TextField
                      fullWidth
                      label="Ore realizate *"
                      name="ore_realizate"
                      type="number"
                      value={formData.ore_realizate}
                      onChange={handleChange}
                      placeholder="ex: 20"
                      required
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                        }
                      }}
                      InputProps={{
                        startAdornment: <AccessTimeIcon color="action" sx={{ mr: 1 }} />,
                        endAdornment: (
                          <Typography variant="caption" sx={{ mr: 1 }}>
                            ore
                          </Typography>
                        ),
                      }}
                    />
                  </Box>

                  {/* Descriere */}
                  <Box sx={{ width: '100%', p: 2 }}>
                    <TextField
                      fullWidth
                      label="Descriere detaliată (opțional)"
                      name="descriere"
                      value={formData.descriere}
                      onChange={handleChange}
                      placeholder="Descrie ce ai făcut, ce ai învățat, cum te-a ajutat..."
                      multiline
                      rows={4}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                        }
                      }}
                      InputProps={{
                        startAdornment: <LightbulbIcon color="action" sx={{ mr: 1, alignSelf: 'flex-start', mt: 1 }} />,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/* Butoane de navigare */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              mt: 5,
              pt: 3,
              borderTop: '1px solid #e0e0e0',
              flexWrap: 'wrap',
              gap: 1,
              rowGap: 1.5,
            }}>
              <Button
                onClick={handleBack}
                disabled={activeStep === 0 || loading}
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                sx={{ borderRadius: 3, px: 4, width: { xs: '100%', sm: 'auto' } }}
              >
                Înapoi
              </Button>

              <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                {activeStep === steps.length - 1 ? (
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <CelebrationIcon />}
                    sx={{ 
                      borderRadius: 3, 
                      px: 5,
                      width: { xs: '100%', sm: 'auto' },
                      background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
                      boxShadow: '0 3px 5px 2px rgba(76, 175, 80, .3)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #43A047 30%, #7CB342 90%)',
                      }
                    }}
                  >
                    {loading ? 'Se trimite...' : 'Trimite cererea'}
                  </Button>
                ) : (
                  <Button 
                    onClick={handleNext}
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ borderRadius: 3, px: 5, width: { xs: '100%', sm: 'auto' } }}
                  >
                    Continuă
                  </Button>
                )}
              </Box>
            </Box>
          </form>
        </Paper>
      </Fade>

      {/* Progres indicator la subsol */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Pasul {activeStep + 1} din {steps.length} • {((activeStep + 1) / steps.length * 100).toFixed(0)}% complet
        </Typography>
      </Box>
    </Container>
  );
}